import { GraphQLError } from "graphql";
import { Button, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { HardwareRequestStatus, HardwareSku } from "src/api/types.generated";
import { errorToast, successToast, Spinner } from "src/shared/components";
import HardwareRequestsHistoryList from "src/shared/components/Hardware/RequestsList";
import { EVENT_ID } from "src/shared/constants/event";
import { buildRequestDataById } from "src/shared/utils/hardware/buildRequestDataById";
import { filterHardwareRequestsBySearch } from "src/shared/utils/hardware/filterHardwareRequestsBySearch";
import { formatHardwareRequestStatus } from "src/shared/utils/hardware/formatHardwareRequestStatus";
import { processRequests } from "src/shared/utils/hardware/processRequests";

import { useCheckHardwareRequestAvailabilityLazyQuery } from "../../graphql/checkHardwareRequestAvailability.generated";
import { useGetHardwareRequestsWithItemsQuery } from "../../graphql/getHardwareRequestsWithItems.generated";
import { useMarkHardwareRequestAsReadyMutation } from "../../graphql/markHardwareRequestAsReady.generated";
import { useRemoveItemsFromHardwareRequestMutation } from "../../graphql/removeItemsFromHardwareRequest.generated";
import { useSplitHardwareRequestByAvailabilityMutation } from "../../graphql/splitHardwareRequestByAvailability.generated";
import { useUpdateHardwareRequestItemQuantitiesMutation } from "../../graphql/updateHardwareRequestItemQuantities.generated";
import { useUpdateHardwareRequestStatusMutation } from "../../graphql/updateHardwareRequestStatus.generated";
import { getRandomHardwareLoadingMessage } from "../../inventory/shared/loadingTextUtil";
import { printHardwarePackage } from "../../shared/printHardwareLabel";
import { useAcceptHardwareRequestIfInStock } from "../../shared/useAcceptHardwareRequestIfInStock";
import StatusFilterTiles from "../StatusFilterTiles";

import CurrentRequestCard from "./components/CurrentRequestCard";
import HardwareQRBottomSheet from "./components/HardwareQRBottomSheet";
import HardwareQRScanner from "./components/HardwareQRScanner";
import HardwareResultBottomSheet from "./components/HardwareResultBottomSheet";
import HasIdConfirmationModal from "./components/HasIdConfirmationModal";
import HardwareDetailsModal from "./detailsModal";
import { useHardwareIdTracking } from "./hooks/useHardwareIdTracking";

type TOrganizerRequestHistoryProps = {
  items: Pick<
    HardwareSku,
    | "code"
    | "image"
    | "type"
    | "name"
    | "location"
    | "description"
    | "category_id"
  >[];
  selectedStatuses: Set<HardwareRequestStatus>;
  hideLottery: boolean;
  query?: string;
  onToggleStatus: (status: HardwareRequestStatus) => void;
  onClearAllStatuses: () => void;
  onSelectAllStatuses: () => void;
  onHideLotteryChange: (checked: boolean) => void;
};

const OrganizerRequestHistory = ({
  items,
  selectedStatuses,
  hideLottery,
  query,
  onToggleStatus,
  onClearAllStatuses,
  onSelectAllStatuses,
  onHideLotteryChange,
}: TOrganizerRequestHistoryProps) => {
  const {
    data: hardwareRequestsData,
    loading: hardwareRequestsLoading,
    refetch: refetchHardwareRequests,
  } = useGetHardwareRequestsWithItemsQuery({
    variables: {
      where: {
        event_id: { equals: EVENT_ID },
      },
    },
  });

  const [editRequestHardwareItem, setEditRequestHardwareItem] = useState<{
    requestId: number;
    skuCode: string;
  } | null>(null);
  const closeModal = () => setEditRequestHardwareItem(null);

  const {
    modalState: hasIdModalState,
    openPromptIfNeeded: openHasIdPromptIfNeeded,
    handleConfirm: handleHasIdConfirm,
  } = useHardwareIdTracking(hardwareRequestsData?.hardwareRequests);

  // QR Scanner states
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showQRBottomSheet, setShowQRBottomSheet] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [isCheckout, setIsCheckout] = useState(true);
  const [scanResult, setScanResult] = useState<{
    success: boolean;
    error?: string;
    data?: {
      markHardwareRequestAsPickedUp?: {
        id: number;
        status: string;
        user_id: number;
        event_id: number;
        user?: { name: string; email: string };
      };
      returnHackerHardwareRequest?: {
        id: number;
        status: string;
        user_id: number;
        event_id: number;
        user?: { name: string; email: string };
        hardware_item_requests?: any[];
      };
    };
  }>({ success: false });

  // Current request state
  const [isLoading, setIsLoading] = useState(false);
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);
  const [targetRequestId, setTargetRequestId] = useState<number | null>(null);

  // Printer settings state
  const [printerEndpoint, setPrinterEndpoint] = useState(
    localStorage.getItem("hardware-printer-endpoint") || ""
  );

  // Mark as ready state
  const [requestIdInput, setRequestIdInput] = useState("");

  // Loading message for spinner
  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  const { acceptIfInStock } = useAcceptHardwareRequestIfInStock();

  const [updateHardwareRequestStatus] =
    useUpdateHardwareRequestStatusMutation();

  const [updateHardwareRequestItemQuantities] =
    useUpdateHardwareRequestItemQuantitiesMutation();
  const [removeItemsFromHardwareRequest] =
    useRemoveItemsFromHardwareRequestMutation();

  const [markAsReady] = useMarkHardwareRequestAsReadyMutation();
  const [splitRequest] = useSplitHardwareRequestByAvailabilityMutation();
  const [checkAvailability, { data: availabilityData }] =
    useCheckHardwareRequestAvailabilityLazyQuery();

  const handlePrinterEndpointChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setPrinterEndpoint(value);
    localStorage.setItem("hardware-printer-endpoint", value);
  };

  // Get navigable requests (REQUESTED, ACCEPTED, or READY requests)
  const navigableRequests = useMemo(() => {
    if (!hardwareRequestsData?.hardwareRequests) return [];

    // Filter by status if provided
    let filteredRequests = hardwareRequestsData.hardwareRequests;
    if (selectedStatuses.size > 0) {
      filteredRequests = hardwareRequestsData.hardwareRequests.filter(
        (request) => selectedStatuses.has(request.status)
      );
    }

    const sortedRequests = filteredRequests
      .slice()
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

    const navigable = sortedRequests.filter(
      (request) =>
        request.status === HardwareRequestStatus.Requested ||
        request.status === HardwareRequestStatus.Accepted
    );

    return navigable;
  }, [hardwareRequestsData, selectedStatuses]);

  // Get current request based on index or target ID
  const currentRequest = useMemo(() => {
    if (navigableRequests.length === 0) return null;

    // If we have a target request ID, try to find it first
    if (targetRequestId) {
      const targetIndex = navigableRequests.findIndex(
        (req) => req.id === targetRequestId
      );
      if (targetIndex !== -1) {
        // Found the target request, update index and clear target
        setCurrentRequestIndex(targetIndex);
        setTargetRequestId(null);
        return navigableRequests[targetIndex];
      }
    }

    // Fall back to index-based selection
    const validIndex = Math.max(
      0,
      Math.min(currentRequestIndex, navigableRequests.length - 1)
    );
    const request = navigableRequests[validIndex];

    // Update index if it was out of bounds
    if (validIndex !== currentRequestIndex) {
      setCurrentRequestIndex(validIndex);
    }

    return request;
  }, [navigableRequests, currentRequestIndex, targetRequestId]);

  // Smart index management when navigable requests change
  useEffect(() => {
    if (navigableRequests.length === 0) {
      setCurrentRequestIndex(0);
      setTargetRequestId(null);
      return;
    }

    // If current index is out of bounds and no target is set, adjust it
    if (currentRequestIndex >= navigableRequests.length && !targetRequestId) {
      setCurrentRequestIndex(Math.max(0, navigableRequests.length - 1));
    }
  }, [navigableRequests, currentRequestIndex, targetRequestId]);

  // Navigation functions
  const handleNextRequest = () => {
    if (currentRequestIndex < navigableRequests.length - 1) {
      setCurrentRequestIndex(currentRequestIndex + 1);
    }
  };

  const handlePreviousRequest = () => {
    if (currentRequestIndex > 0) {
      setCurrentRequestIndex(currentRequestIndex - 1);
    }
  };

  // Helper functions
  const handleCheckout = () => {
    setIsCheckout(true);
    setShowQRBottomSheet(true);
  };

  const handleReturn = () => {
    setIsCheckout(false);
    setShowQRBottomSheet(true);
  };

  const handleAcceptCurrentRequest = async () => {
    if (!currentRequest) return;

    setIsLoading(true);
    try {
      // Set target to maintain position after status change
      setTargetRequestId(currentRequest.id);
      await acceptIfInStock(currentRequest.id);
      await refetchHardwareRequests();
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkReady = async () => {
    if (!currentRequest) return;

    setIsLoading(true);
    try {
      // Set target to maintain position after status change
      setTargetRequestId(currentRequest.id);
      await markAsReady({
        variables: { id: currentRequest.id },
      });
      await refetchHardwareRequests();
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsReady = async (requestId: number) => {
    setIsLoading(true);
    try {
      await markAsReady({
        variables: { id: requestId },
      });
      await refetchHardwareRequests();
      successToast(`Request #${requestId} marked as ready successfully!`);
    } catch (e) {
      errorToast(
        (e as GraphQLError)?.message ||
          `Failed to mark request #${requestId} as ready`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefetchRequests = async () => {
    setIsLoading(true);
    try {
      await refetchHardwareRequests();
      successToast("Requests refreshed successfully!");
    } catch (e) {
      errorToast((e as GraphQLError)?.message || "Failed to refresh requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSplitRequest = async () => {
    if (!currentRequest) return;

    setIsLoading(true);
    try {
      const result = await splitRequest({
        variables: { original_request_id: currentRequest.id },
      });

      if (result.data?.splitHardwareRequestByAvailability) {
        successToast(
          "Request split successfully! A new request was created with available items."
        );
        await refetchHardwareRequests();
      }
    } catch (e) {
      const errorMessage = (e as GraphQLError)?.message;
      errorToast(errorMessage || "Failed to split request");

      // Log detailed error for debugging
      console.error("Split request error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckAvailability = async () => {
    if (!currentRequest) return;

    setIsLoading(true);
    try {
      await checkAvailability({
        variables: { request_id: currentRequest.id },
      });

      // The useEffect will handle setIsLoading(false) when availabilityData changes
      // But if for some reason it doesn't, we have a fallback
      setTimeout(() => {
        setIsLoading(false);
      }, 2000); // 2 second fallback
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
      setIsLoading(false);
    }
  };

  const handleDenyCurrentRequest = async () => {
    if (!currentRequest) return;

    setIsLoading(true);
    try {
      // For deny, we don't set target since denied requests are no longer navigable
      await updateHardwareRequestStatus({
        variables: {
          id: currentRequest.id,
          status: HardwareRequestStatus.Denied,
        },
      });
      await refetchHardwareRequests();
      successToast("Request denied successfully");
      // Index will be automatically adjusted by the smart navigation logic
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRequestStatus = async (
    requestId: number,
    status: HardwareRequestStatus
  ) => {
    const request = hardwareRequestsData?.hardwareRequests?.find(
      (req) => req.id === requestId
    );

    try {
      await updateHardwareRequestStatus({
        variables: {
          id: requestId,
          status: status,
        },
      });
      await refetchHardwareRequests();

      const statusText = formatHardwareRequestStatus(status);
      successToast(`Request #${requestId} status updated to ${statusText}`);

      if (
        request &&
        (status === HardwareRequestStatus.PickedUp ||
          status === HardwareRequestStatus.Returned)
      ) {
        openHasIdPromptIfNeeded(
          status === HardwareRequestStatus.PickedUp ? "checkout" : "return",
          requestId,
          request.user_id,
          request.event_id,
          request.user?.name ?? "this user"
        );
      }
    } catch (e) {
      errorToast(
        (e as GraphQLError)?.message || "Failed to update request status"
      );
    }
  };

  const handleReturnSuccess = (requestId: number) => {
    const request = hardwareRequestsData?.hardwareRequests?.find(
      (req) => req.id === requestId
    );
    if (!request) return;

    openHasIdPromptIfNeeded(
      "return",
      requestId,
      request.user_id,
      request.event_id,
      request.user?.name ?? "this user"
    );
  };

  // Handle availability data when it changes
  useEffect(() => {
    if (availabilityData?.checkHardwareRequestAvailability) {
      const availability = availabilityData.checkHardwareRequestAvailability;
      const message = availability.can_approve_fully
        ? `All ${availability.total_items} items are available for this request`
        : `Only ${availability.available_items} of ${
            availability.total_items
          } items are available. ${
            availability.total_items - availability.available_items
          } items have insufficient quantity.`;

      // Use success toast for full availability, error toast for partial/unavailable
      if (availability.can_approve_fully) {
        successToast(message);
      } else {
        errorToast(message);
      }

      setIsLoading(false);
    }
  }, [availabilityData]);

  // Cleanup effect to ensure loading state is reset when component unmounts or request changes
  const currentRequestId = currentRequest?.id;
  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, [currentRequestId]);

  // Prompt for ID tracking after a successful QR scan checkout/return
  useEffect(() => {
    if (!scanResult.success) return;

    const pickedUp = scanResult.data?.markHardwareRequestAsPickedUp;
    const returned = scanResult.data?.returnHackerHardwareRequest;

    if (pickedUp) {
      openHasIdPromptIfNeeded(
        "checkout",
        pickedUp.id,
        pickedUp.user_id,
        pickedUp.event_id,
        pickedUp.user?.name ?? "this user"
      );
    } else if (returned) {
      openHasIdPromptIfNeeded(
        "return",
        returned.id,
        returned.user_id,
        returned.event_id,
        returned.user?.name ?? "this user"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResult]);

  const handleRemoveItem = useCallback(
    async (requestId: number, hardwareItemId: number) => {
      const request = hardwareRequestsData?.hardwareRequests?.find(
        (req) => req.id === requestId
      );
      if ((request?.hardware_item_requests?.length ?? 0) <= 1) {
        errorToast("Cannot remove the last item in a request");
        return;
      }

      try {
        await removeItemsFromHardwareRequest({
          variables: {
            request_id: requestId,
            item_ids: [hardwareItemId],
          },
        });
        await refetchHardwareRequests();
        successToast("Item removed from request successfully");
      } catch (e) {
        errorToast((e as any)?.message || "Failed to remove item from request");
      }
    },
    [
      removeItemsFromHardwareRequest,
      refetchHardwareRequests,
      hardwareRequestsData,
    ]
  );

  const hardwareRequests = useMemo(() => {
    // Sort requests by created_at in descending order (newest first)
    const sortedRequests = filterHardwareRequestsBySearch(
      hardwareRequestsData?.hardwareRequests
        ?.slice()
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
      query,
      items
    );

    return processRequests({
      data: sortedRequests,
      items,
      userType: "organizer",
      getButtons: (requestStatus) => {
        // Remove all buttons from individual items - they should be at request level
        return [];
      },
      onClick: (requestId, skuCode) => {
        setEditRequestHardwareItem({ requestId, skuCode });
      },
      hideLottery,
      onQuantityIncrease: async (
        requestId,
        hardwareItemId,
        currentQuantity
      ) => {
        try {
          await updateHardwareRequestItemQuantities({
            variables: {
              request_id: requestId,
              updates: [
                {
                  hardware_item_id: hardwareItemId,
                  new_quantity: currentQuantity + 1,
                },
              ],
            },
          });
          await refetchHardwareRequests();
        } catch (e) {
          errorToast((e as any)?.message || "Failed to update quantity");
        }
      },
      onQuantityDecrease: async (
        requestId,
        hardwareItemId,
        currentQuantity
      ) => {
        if (currentQuantity <= 1) {
          const request = hardwareRequestsData?.hardwareRequests?.find(
            (req) => req.id === requestId
          );
          if ((request?.hardware_item_requests?.length ?? 0) <= 1) {
            errorToast("Cannot remove the last item in a request");
          }
          return;
        }
        try {
          await updateHardwareRequestItemQuantities({
            variables: {
              request_id: requestId,
              updates: [
                {
                  hardware_item_id: hardwareItemId,
                  new_quantity: currentQuantity - 1,
                },
              ],
            },
          });
          await refetchHardwareRequests();
        } catch (e) {
          errorToast((e as any)?.message || "Failed to update quantity");
        }
      },
      onRemoveItem: handleRemoveItem,
    });
  }, [
    hardwareRequestsData,
    items,
    hideLottery,
    query,
    updateHardwareRequestItemQuantities,
    refetchHardwareRequests,
    handleRemoveItem,
  ]);

  const handlePrintLabel = async (requestId: number, requestData: any) => {
    if (!printerEndpoint) {
      errorToast("Printer endpoint not configured");
      return;
    }

    try {
      // Find the request in the hardware requests data
      const request = hardwareRequestsData?.hardwareRequests?.find(
        (req) => req.id === requestId
      );
      if (!request) {
        errorToast("Request not found");
        return;
      }

      // Get the processed request data to access the items with full details
      const processedRequest = hardwareRequests.find((req) =>
        req.items.some((item) => item.requestId === requestId)
      );
      if (!processedRequest) {
        errorToast("Request data not found");
        return;
      }

      // Filter items to only include those from this specific request
      const requestItems = processedRequest.items.filter(
        (item) => item.requestId === requestId
      );

      // Debug logging
      console.log(`Printing request #${requestId}:`, {
        totalItemsInGroup: processedRequest.items.length,
        filteredItems: requestItems.length,
        requestItems: requestItems.map((item) => ({
          skuCode: item.skuCode,
          title: item.title,
          quantity: item.quantity,
          requestId: item.requestId,
        })),
      });

      // Prepare the data for the print service
      const printData = {
        requestId: requestId,
        userName: requestData?.user?.name || "Unknown",
        userEmail: requestData?.user?.email || "Unknown",
        hasLotteryItems: requestItems.some((item) =>
          item.label?.some((label) => label.text === "Special Item")
        ),
        items: requestItems.map((item) => ({
          title: item.title,
          description: item.description,
          label: item.label,
          quantity: item.quantity,
        })),
      };

      await printHardwarePackage(printData);

      successToast("Label sent to printer successfully");
    } catch (e) {
      errorToast(`Failed to print label: ${(e as Error)?.message}`);
    }
  };

  // Calculate status counts
  const statusCounts = useMemo(() => {
    if (!hardwareRequestsData?.hardwareRequests) return {};

    const counts: Record<HardwareRequestStatus, number> = {
      [HardwareRequestStatus.Waiting]: 0,
      [HardwareRequestStatus.Requested]: 0,
      [HardwareRequestStatus.Accepted]: 0,
      [HardwareRequestStatus.Ready]: 0,
      [HardwareRequestStatus.PickedUp]: 0,
      [HardwareRequestStatus.Returned]: 0,
      [HardwareRequestStatus.Denied]: 0,
      [HardwareRequestStatus.Cancelled]: 0,
    };

    hardwareRequestsData.hardwareRequests.forEach((request) => {
      counts[request.status]++;
    });

    return counts;
  }, [hardwareRequestsData]);

  // Show loading spinner while fetching hardware requests
  if (hardwareRequestsLoading) {
    return <Spinner text={loadingMessage} />;
  }

  return (
    <>
      <StatusFilterTiles
        organizerMode
        statusCounts={statusCounts}
        selectedStatuses={selectedStatuses}
        onToggleStatus={onToggleStatus}
        onClearAllStatuses={onClearAllStatuses}
        onSelectAllStatuses={onSelectAllStatuses}
        hideLottery={hideLottery}
        onHideLotteryChange={onHideLotteryChange}
      >
        {/* Printer Settings + Mark as Ready Row */}
        <MarkReadyRow>
          <PrinterInputWrapper>
            <TextInput
              placeholder="Enter printer endpoint URL (e.g., http://localhost:5001)"
              value={printerEndpoint}
              onChange={handlePrinterEndpointChange}
              style={{
                width: "100%",
                height: "38px",
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
            />
          </PrinterInputWrapper>
          <RightGroup>
            <Button
              color="secondary"
              size="md"
              onClick={handleRefetchRequests}
              disabled={isLoading}
            >
              {isLoading ? "Refreshing..." : "Refresh Requests"}
            </Button>
            <MarkReadyContainer>
              <MarkReadyInputContainer>
                <TextInput
                  placeholder="Enter request ID..."
                  value={requestIdInput}
                  onChange={(e) => setRequestIdInput(e.target.value)}
                  style={{
                    flex: 1,
                    marginRight: "8px",
                    height: "38px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  }}
                  type="number"
                />
                <Button
                  color="primary"
                  size="sm"
                  style={{ width: "110px", height: "38px", flexShrink: 0 }}
                  onClick={() => {
                    const requestId = parseInt(requestIdInput);
                    if (requestId && !isNaN(requestId)) {
                      handleMarkAsReady(requestId);
                      setRequestIdInput(""); // Clear the input after marking as ready
                    }
                  }}
                  disabled={
                    isLoading ||
                    !requestIdInput ||
                    isNaN(parseInt(requestIdInput))
                  }
                >
                  Mark Ready
                </Button>
              </MarkReadyInputContainer>
            </MarkReadyContainer>
          </RightGroup>
        </MarkReadyRow>
      </StatusFilterTiles>

      {editRequestHardwareItem !== null && (
        <HardwareDetailsModal
          open={editRequestHardwareItem !== null}
          closeModal={closeModal}
          requestId={editRequestHardwareItem.requestId}
          skuCode={editRequestHardwareItem.skuCode}
          itemDetails={{
            name:
              items.find(
                (item) => item.code === editRequestHardwareItem.skuCode
              )?.name ?? "Unknown Item",
            location:
              items.find(
                (item) => item.code === editRequestHardwareItem.skuCode
              )?.location ?? "Unknown Location",
          }}
        />
      )}

      {/* QR Scanner Components */}
      <HardwareQRBottomSheet
        isOpen={showQRBottomSheet}
        isCheckout={isCheckout}
        setShowCameraModal={setShowQRScanner}
        setShowScannerModal={setShowQRBottomSheet}
      />

      <HardwareQRScanner
        showCameraModal={showQRScanner}
        isCheckout={isCheckout}
        setShowCameraModal={setShowQRScanner}
        setShowResultModal={setShowResultModal}
        setScanResult={setScanResult}
      />

      <HardwareResultBottomSheet
        isOpen={showResultModal}
        isCheckout={isCheckout}
        result={scanResult}
        setShowResultModal={setShowResultModal}
      />

      <HasIdConfirmationModal
        open={hasIdModalState !== null}
        modalState={hasIdModalState}
        onConfirm={handleHasIdConfirm}
      />

      {/* Current Request Card */}
      <CurrentRequestCard
        request={currentRequest}
        items={items}
        onAccept={handleAcceptCurrentRequest}
        onMarkReady={handleMarkReady}
        onSplitRequest={handleSplitRequest}
        onCheckAvailability={handleCheckAvailability}
        onDeny={handleDenyCurrentRequest}
        onNext={handleNextRequest}
        onPrevious={handlePreviousRequest}
        canGoNext={currentRequestIndex < navigableRequests.length - 1}
        canGoPrevious={currentRequestIndex > 0}
        currentIndex={currentRequestIndex + 1}
        totalCount={navigableRequests.length}
        isLoading={isLoading}
        onRefetchRequest={refetchHardwareRequests}
      />

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton onClick={handleCheckout}>Checkout Hardware</ActionButton>
        <ActionButton onClick={handleReturn}>Return Hardware</ActionButton>
      </ActionButtons>

      <HardwareRequestsHistoryList
        hardwareRequests={hardwareRequests
          .filter((request) => {
            // Hide waiting requests by default, unless explicitly filtered for
            if (
              request.status === HardwareRequestStatus.Waiting &&
              !selectedStatuses.has(HardwareRequestStatus.Waiting)
            ) {
              return false;
            }
            // Apply status filter if provided
            return selectedStatuses.size > 0
              ? selectedStatuses.has(request.status)
              : true;
          })
          .map((request) => ({
            status: request.status,
            items: request.items,
            requestDataById: buildRequestDataById(
              request.items.map((item) => item.requestId),
              hardwareRequestsData?.hardwareRequests
            ),
          }))}
        userType="organizer"
        onPrintLabel={handlePrintLabel}
        onUpdateStatus={handleUpdateRequestStatus}
        onRefresh={refetchHardwareRequests}
        onReturnSuccess={handleReturnSuccess}
        printerEndpoint={printerEndpoint}
        isLoading={isLoading}
      />
    </>
  );
};

const PrinterInputWrapper = styled.div`
  width: 480px;
  flex-shrink: 0;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.globalConstants.color.brandPrimary};
  background: white;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.globalConstants.color.brandPrimary};
    color: white;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const MarkReadyRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
`;

const MarkReadyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 400px;
`;

const MarkReadyInputContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export default OrganizerRequestHistory;
